import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Button } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { toast } from "react-hot-toast";
import { TableOrder, tableOrderService } from "@/api/services/table-order.service";

const statusLabels: Record<string, string> = {
  NEW: "신규",
  ACCEPTED: "접수",
  SERVED: "서빙완료",
  CANCELED: "취소",
};

const fulfillmentTypeLabels: Record<string, string> = {
  INSTANT: "즉시 제공",
  KITCHEN: "제조 필요",
};

const formatTableLabel = (order: TableOrder) => {
  if (order.table_name && order.table_code) {
    return `${order.table_name} (${order.table_code})`;
  }

  if (order.order_type === "pickup") {
    return "픽업 주문";
  }

  return "테이블 미지정";
};

const canCancelOrderFromStatusAction = (order: TableOrder) => {
  return (
    (order.status === "NEW" || order.status === "ACCEPTED") &&
    (order.payment_status === "UNPAID" || order.payment_status === "PAYMENT_FAILED")
  );
};

const OrderDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState<TableOrder | null>(null);
  const [loading, setLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await tableOrderService.getTableOrder(Number(id));
      setOrder(response.data);
    } catch (error) {
      toast.error("주문 상세 정보를 불러오지 못했습니다.");
      navigate("/table-order/orders");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleChangeStatus = async (nextStatus: "ACCEPTED" | "SERVED" | "CANCELED") => {
    if (!order) return;

    try {
      await tableOrderService.updateTableOrderStatus(order.id, nextStatus);
      await loadDetail();
      toast.success("주문 상태가 변경되었습니다.");
    } catch (error) {
      toast.error("주문 상태 변경에 실패했습니다.");
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="주문 상세" pageTitle="현장주문" />

        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">{order ? `주문 #${order.id}` : "주문 상세"}</h5>
            <div className="d-flex gap-2">
              <Button color="light" onClick={() => navigate("/table-order/orders")}>목록</Button>
              {order && order.status === "NEW" && (
                <Button color="soft-success" onClick={() => handleChangeStatus("ACCEPTED")}>접수</Button>
              )}
              {order && order.status === "ACCEPTED" && (
                <Button color="soft-info" onClick={() => handleChangeStatus("SERVED")}>서빙완료</Button>
              )}
              {order && (order.status === "NEW" || order.status === "ACCEPTED") && (
                <Button
                  color="soft-danger"
                  disabled={!canCancelOrderFromStatusAction(order)}
                  onClick={() => handleChangeStatus("CANCELED")}
                >
                  취소
                </Button>
              )}
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <p className="text-muted mb-0">불러오는 중...</p>
            ) : !order ? (
              <p className="text-muted mb-0">주문 정보가 없습니다.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div><strong>주문 타입:</strong> {order.order_type === "pickup" ? "픽업" : "서빙"}</div>
                  <div><strong>제공 타입:</strong> {fulfillmentTypeLabels[order.fulfillment_type || ""] || order.fulfillment_type || "-"}</div>
                  <div><strong>상태:</strong> {statusLabels[order.status] || order.status}</div>
                  <div><strong>테이블:</strong> {formatTableLabel(order)}</div>
                  <div><strong>주문자:</strong> {order.created_by_phone}</div>
                  <div><strong>총 금액:</strong> {order.total_amount.toLocaleString()}원</div>
                  <div><strong>생성시각:</strong> {new Date(order.created_at).toLocaleString()}</div>
                </div>

                <p className="mb-3"><strong>메모:</strong> {order.memo || "-"}</p>

                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>메뉴</th>
                        <th>수량</th>
                        <th>합계</th>
                        <th>옵션</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item) => (
                        <tr key={item.id}>
                          <td>{item.menu_name_snapshot}</td>
                          <td>{item.quantity}</td>
                          <td>{item.line_amount.toLocaleString()}원</td>
                          <td>
                            {item.options.length === 0
                              ? "-"
                              : item.options.map((option) => `${option.option_name_snapshot}(+${option.extra_price_snapshot})`).join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default OrderDetail;
